import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MemberDietService } from '../shared/services/member-diet.service';

/**
 * Member-facing guard for /my-diet-plan.
 *
 * Mirrors the drawer-link visibility rule:
 * - Drawer link is hidden unless `clubsService.flags()?.diets &&
 *   memberDiet.hasActivePlan()`.
 * - This guard enforces the same rule against direct URL access (deep links,
 *   bookmarks, manual address-bar typing, push-notification routes that fire
 *   before the plan is approved, etc.).
 *
 * Order: this guard runs AFTER AuthGuard / IsVerifiedGuard / featureFlagGuard
 * (those handle unauthenticated, unverified, and feature-off cases). By the
 * time we get here we know the user is verified and the diets flag is on —
 * the only remaining check is whether they actually have an approved plan.
 *
 * If `MemberDietService` hasn't loaded yet (cold direct-navigation), we fire
 * `loadMyPlan()` and await the result before deciding.
 */
export const hasMyDietPlanGuard: CanActivateFn = async () => {
  const memberDiet = inject(MemberDietService);
  const router = inject(Router);

  if (!memberDiet.loaded()) {
    await memberDiet.loadMyPlan();
  }

  if (memberDiet.hasActivePlan()) {
    return true;
  }

  // No approved plan — bounce to home rather than render an empty state.
  await router.navigate(['/']);
  return false;
};
